import { useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";
import { AuthContext } from "../Context/Context";
import { useNavigate } from "react-router-dom";
import { Localstorage } from "../service/Localstorage";
import { API_URL } from "@utils/constants";
import jwtDecode from "jwt-decode";

export const useApplication = () => {
  const navigate = useNavigate();

  const { user, setUser, details, setDetails } =
      useContext(AuthContext);

  const [authorized, setAuthorized] = useState("WAITING");

  /**
   * Clear local authentication state.
   */
  const clearAuth = useCallback(() => {
    setUser(null);
    Localstorage().clearAuth();
    setAuthorized("UNAUTHORIZED");
  }, [setUser]);

  /**
   * Login
   */
  const login = useCallback(
      async (payload) => {
        try {
          const response = await axios.post(
              `${API_URL}/auth/login`,
              payload
          );

          const data = response?.data?.data;

          if (!data) {
            return {
              res: "Réponse serveur invalide",
              error: true,
            };
          }

          if (data.error) {
            return {
              res: data.details,
              error: true,
            };
          }

          const accessToken = data.details?.accessToken;
          const refreshToken = data.details?.refreshToken;

          if (!accessToken || !refreshToken) {
            console.error("Tokens manquants dans la réponse login");

            return {
              res: "Tokens manquants",
              error: true,
            };
          }

          const userData = {
            token: accessToken,
            refresh: refreshToken,
            ...jwtDecode(accessToken),
          };

          /**
           * Save authentication BEFORE changing the state.
           */
          Localstorage().authenticate(userData);

          setUser(userData);
          setAuthorized("AUTHORIZED");

          return {
            res: null,
            error: false,
          };
        } catch (error) {
          console.error("Login error:", error);

          return {
            res: error,
            error: true,
          };
        }
      },
      [setUser]
  );

  /**
   * Refresh the current access token.
   */
  const refreshAuth = useCallback(async () => {
    const authData = Localstorage().isAuth();

    if (!authData) {
      clearAuth();
      return false;
    }

    let parsedUser;

    try {
      parsedUser = JSON.parse(authData);
    } catch (error) {
      console.error("Invalid authentication data:", error);

      clearAuth();
      return false;
    }

    if (!parsedUser?.refresh) {
      clearAuth();
      return false;
    }

    try {
      const response = await axios.post(
          `${API_URL}/auth/refresh`,
          {
            refreshToken: parsedUser.refresh,
          }
      );

      const data = response?.data?.data;

      if (!data || data.error) {
        throw new Error("Session expirée");
      }

      const accessToken = data.details?.accessToken;

      if (!accessToken) {
        throw new Error("Access token manquant");
      }

      /**
       * Some APIs rotate the refresh token.
       * Keep the old one if the API doesn't return a new one.
       */
      const refreshToken =
          data.details?.refreshToken || parsedUser.refresh;

      const userData = {
        ...parsedUser,
        token: accessToken,
        refresh: refreshToken,
        ...jwtDecode(accessToken),
      };

      Localstorage().authenticate(userData);
      setUser(userData);
      setAuthorized("AUTHORIZED");

      return true;
    } catch (error) {
      console.error("Refresh auth error:", error);

      clearAuth();

      return false;
    }
  }, [clearAuth, setUser]);

  /**
   * Check authentication when the application starts.
   */
  useEffect(() => {
    let mounted = true;

    const initializeAuth = async () => {
      const authData = Localstorage().isAuth();

      /**
       * No stored session.
       */
      if (!authData) {
        if (!mounted) return;

        setUser(null);
        setAuthorized("UNAUTHORIZED");

        return;
      }

      /**
       * There is a stored session.
       * Try to refresh it.
       */
      const success = await refreshAuth();

      if (!mounted) return;

      if (!success) {
        setAuthorized("UNAUTHORIZED");
      }
    };

    initializeAuth();

    return () => {
      mounted = false;
    };
  }, [refreshAuth, setUser]);

  /**
   * Logout
   */
  const logout = useCallback(async () => {
    const authData = Localstorage().isAuth();

    try {
      if (authData) {
        const parsedUser = JSON.parse(authData);

        if (parsedUser?.refresh) {
          await axios.post(`${API_URL}/auth/logout`, {
            refreshToken: parsedUser.refresh,
          });
        }
      }
    } catch (error) {
      /**
       * Logout should still succeed locally
       * even if the API request fails.
       */
      console.error("Erreur lors du logout API:", error);
    } finally {
      clearAuth();
      navigate("/");
    }
  }, [clearAuth, navigate]);

  return {
    user,
    login,
    logout,
    authorized,
    setAuthorized,
    details,
    setDetails,
    refreshAuth,
  };
};
